import React, { useState } from 'react';
import moment from 'moment-timezone';
import { CalendarClock, CalendarX2, Loader2, Phone, UserX, Video, X } from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import { useMeetingAction } from '../../../services/calendlyBooking';

// A booked meeting and what staff can do with it (in-app meetings, Nic
// 2026-10-25): Reschedule or Cancel while it's ahead, They missed it once it
// has started, Join for a video meeting. Shared by the Onboarding Workspace
// and Sales. Old Calendly meetings (provider 'calendly') only show: Calendly
// still owns them until they're past.
//
// Cancel and Missed ask first in their own panel under the row, full width
// (Nic 2026-10-25: the inline question was janky): what happens, then Keep
// it / Yes on the right, stacking on a phone.

const BTN =
  'inline-flex h-[28px] items-center gap-[6px] whitespace-nowrap rounded-lg px-[10px] text-xs font-semibold outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50';
const SECONDARY = cn(
  BTN,
  'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-800 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10 dark:hover:text-white',
);
const JOIN = cn(BTN, 'bg-sky-600 text-white no-underline hover:bg-sky-500 hover:text-white');

export const meetingWhen = (at) => {
  const m = moment(at);
  if (m.isSame(moment(), 'day')) return `today at ${m.format('h:mm A')}`;
  if (m.isSame(moment().add(1, 'day'), 'day')) return `tomorrow at ${m.format('h:mm A')}`;
  return m.format('ddd, MMM D [at] h:mm A');
};

export const kindLabel = (meeting) => (meeting?.kind === 'video' ? 'Video meeting' : 'Phone call');

// Booked in Calendly before launch (Nic 2026-10-27): Calendly still owns it
// until it's past (moves and cancels happen there and land here through the
// webhook), so every place a meeting shows carries this small tag while any
// are left. In-app meetings (provider 'app') never show it.
export const isLegacy = (meeting) => !!meeting && (meeting.provider || 'calendly') === 'calendly';
// Their own Outlook appointment on the Schedule (Nic 2026-10-28): it holds
// the time in the picker, and is managed in Outlook, never here.
export const isOutlook = (item) => !!item && item.provider === 'outlook';
export const OUTLOOK_HINT = 'From your Outlook calendar. It holds this time in the picker. Change it in Outlook.';
export const OutlookTag = ({ className }) => (
  <span
    title={OUTLOOK_HINT}
    className={cn(
      'inline-flex h-[16px] shrink-0 items-center rounded px-[5px] align-middle text-[10px] font-bold uppercase tracking-wide text-sky-700/80 ring-1 ring-inset ring-sky-200 dark:text-sky-300/80 dark:ring-sky-400/30',
      className,
    )}
  >
    Outlook
  </span>
);
// Lunch on the calendar (Nic 2026-11-02): a host's lunch, per working day,
// the same lunch the picker greys out. Set on the Schedule calendar.
export const isLunch = (item) => !!item && item.provider === 'lunch';
export const LUNCH_HINT = 'Their lunch, the same block the picker holds. Set on the Schedule calendar.';
export const LunchTag = ({ className }) => (
  <span
    title={LUNCH_HINT}
    className={cn(
      'inline-flex h-[16px] shrink-0 items-center rounded px-[5px] align-middle text-[10px] font-bold uppercase tracking-wide text-violet-700/80 ring-1 ring-inset ring-violet-200 dark:text-violet-300/80 dark:ring-violet-400/30',
      className,
    )}
  >
    Lunch
  </span>
);
export const LEGACY_HINT =
  'Booked in Calendly before launch. Move or cancel it in Calendly and it updates here. To switch them to an in-app meeting, cancel it in Calendly first, then Book.';
export const LegacyTag = ({ className }) => (
  <span
    title={LEGACY_HINT}
    className={cn(
      'inline-flex h-[16px] shrink-0 items-center rounded px-[5px] align-middle text-[10px] font-bold uppercase tracking-wide text-slate-400 ring-1 ring-inset ring-slate-200 dark:text-slate-500 dark:ring-white/15',
      className,
    )}
  >
    Calendly
  </span>
);

// Join opens from 15 minutes before until half an hour after it ends.
// Shared with the meeting alerts and the Schedule rail (Nic 2026-10-27).
export const joinable = (m, now = Date.now()) =>
  m?.kind === 'video' &&
  !!m.videoUrl &&
  now >= new Date(m.startTime).getTime() - 15 * 60000 &&
  now <= new Date(m.endTime || m.startTime).getTime() + 30 * 60000;

const ASK = {
  cancel: {
    icon: CalendarX2,
    title: (m) => `Cancel this ${m.kind === 'video' ? 'video meeting' : 'call'}?`,
    body: (m) => `${meetingWhen(m.startTime)[0].toUpperCase()}${meetingWhen(m.startTime).slice(1)}. They get a text and an email saying it's canceled.`,
    yes: 'Yes, cancel it',
    tone: {
      panel: 'bg-rose-50 ring-rose-200 dark:!bg-rose-500/10 dark:ring-rose-400/20',
      chip: 'bg-rose-100 text-rose-600 dark:!bg-rose-500/20 dark:text-rose-300',
      yes: 'bg-rose-600 text-white hover:bg-rose-500',
    },
  },
  missed: {
    icon: UserX,
    title: () => 'Mark it missed?',
    body: () => 'Their card shows the missed meeting. Book them again once you reach them.',
    yes: 'Yes, they missed it',
    tone: {
      panel: 'bg-amber-50 ring-amber-200 dark:!bg-amber-500/10 dark:ring-amber-400/20',
      chip: 'bg-amber-100 text-amber-700 dark:!bg-amber-500/20 dark:text-amber-300',
      yes: 'bg-amber-600 text-white hover:bg-amber-500',
    },
  },
};

/**
 * `meeting` (meetingView shape), `mount` 'board' | 'onboarding', `leadId`
 * (the onboardingId for 'onboarding'), `canAct`, `onReschedule(meeting)`.
 * Optional `label` (shown first on the row), `extra` (more buttons at the
 * end of the row) and `hideActions` (the caller is showing its own form).
 */
const MeetingActions = ({ meeting, mount, leadId, canAct, onReschedule, className, label, extra, hideActions }) => {
  const [asking, setAsking] = useState(null); // 'cancel' | 'missed'
  const action = useMeetingAction({
    onSuccess: (_, vars) => {
      setAsking(null);
      toast.success(vars.action === 'cancel' ? 'Canceled. They get a text and email saying so.' : 'Marked missed.');
    },
    onError: (err) => toast.error(err?.response?.data?.msg || "That didn't go through. Try again."),
  });
  if (!meeting) return null;
  const now = Date.now();
  const ahead = new Date(meeting.startTime).getTime() > now;
  const inApp = meeting.provider === 'app';
  const KindIcon = meeting.kind === 'video' ? Video : Phone;
  const ask = asking ? ASK[asking] : null;
  const AskIcon = ask?.icon;
  const showButtons = !hideActions && !asking;

  return (
    // The row by its own width (Nic 2026-10-30), not the window's: wide, the
    // words and the buttons share one line; under 48rem the buttons take a
    // row of their own and spread evenly, and the words never squeeze to one
    // word per line (they keep 14rem and wrap the buttons down instead).
    <div className={cn('w-full [container-type:inline-size]', className)}>
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[8px]">
        {label}
        <span className="inline-flex min-w-0 flex-1 basis-[14rem] items-center gap-[6px] text-[13px] leading-snug text-slate-600 dark:text-slate-300">
          <KindIcon size={14} className="shrink-0 text-sky-600 dark:text-sky-400" />
          <span className="min-w-0">
            <b className="font-semibold text-slate-800 dark:text-slate-100">{inApp ? kindLabel(meeting) : 'Meeting'}</b>
            {inApp ? null : <LegacyTag className="mx-[6px]" />}{' '}
            {meetingWhen(meeting.startTime)}
            {meeting.hostName ? ` with ${meeting.hostName}` : ''}
          </span>
        </span>
        {showButtons && ((canAct && inApp) || extra) ? (
          <span className={cn(
            'flex basis-full flex-wrap gap-[6px] [&>*]:flex-1 [&>*]:justify-center',
            '[@container(min-width:48rem)]:ml-auto [@container(min-width:48rem)]:basis-auto [@container(min-width:48rem)]:justify-end [@container(min-width:48rem)]:[&>*]:flex-none',
          )}>
            {canAct && inApp && joinable(meeting, now) ? (
              <a href={meeting.videoUrl} target="_blank" rel="noreferrer" className={JOIN}>
                <Video size={13} /> Join
              </a>
            ) : null}
            {canAct && inApp && ahead ? (
              <>
                <button type="button" onClick={() => onReschedule?.(meeting)} className={SECONDARY}>
                  <CalendarClock size={13} /> Reschedule
                </button>
                <button type="button" onClick={() => setAsking('cancel')} className={SECONDARY}>
                  <X size={13} /> Cancel
                </button>
              </>
            ) : null}
            {canAct && inApp && !ahead ? (
              <button type="button" onClick={() => setAsking('missed')} className={SECONDARY}>
                <UserX size={13} /> They missed it
              </button>
            ) : null}
            {extra}
          </span>
        ) : null}
      </div>
      {/* The one line that says what to do with a Calendly meeting (owner
          only, while it's ahead): there are no buttons for it here. */}
      {!inApp && canAct && ahead && !hideActions ? (
        <div className="mt-[4px] text-[12px] leading-snug text-slate-400 dark:text-slate-500">{LEGACY_HINT}</div>
      ) : null}

      {ask ? (
        <div
          role="alertdialog"
          aria-label={ask.title(meeting)}
          className={cn(
            'mt-[8px] flex flex-col gap-[10px] rounded-xl px-[12px] py-[10px] ring-1 sm:flex-row sm:items-center',
            ask.tone.panel,
          )}
        >
          <div className="flex min-w-0 flex-1 items-start gap-[10px]">
            <span className={cn('flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg', ask.tone.chip)}>
              <AskIcon size={15} />
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 dark:text-white">{ask.title(meeting)}</div>
              <div className="text-[12.5px] leading-snug text-slate-600 dark:text-slate-300">{ask.body(meeting)}</div>
            </div>
          </div>
          <div className="flex shrink-0 justify-end gap-[6px]">
            <button type="button" onClick={() => setAsking(null)} disabled={action.isLoading} className={SECONDARY}>
              Keep it
            </button>
            <button
              type="button"
              disabled={action.isLoading}
              onClick={() =>
                action.mutate({ mount, leadId, bookingId: meeting.calendlyBookingId, action: asking })
              }
              className={cn(BTN, ask.tone.yes)}
            >
              {action.isLoading ? <Loader2 size={13} className="animate-spin" /> : null}
              {ask.yes}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MeetingActions;
